import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { LOCALES, setLocale, t, useLocale } from '@/i18n';

import { CheckIcon, ChevronDownIcon, GlobeIcon } from './Icons';
import styles from './LanguagePicker.module.css';
import { Select } from './Select';

/**
 * Each language is named in itself (日本語, العربية, हिन्दी), so a person who cannot read the page's current language
 * still finds theirs. `lang` and `dir` on each name keep its script and direction right whatever the page speaks.
 */
function Name({ code }: { readonly code: string }): React.JSX.Element {
  const locale = LOCALES.find((item) => item.code === code);
  return (
    <span className={styles.name} lang={code} dir={locale?.dir ?? 'ltr'}>
      {locale?.name ?? code}
    </span>
  );
}

/** The footer's picker: a small pill with a globe and the current language; it opens a list of all of them. */
export function LanguageMenu(): React.JSX.Element {
  const current = useLocale();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    list.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        root.current?.querySelector<HTMLButtonElement>('button')?.focus();
      }
    };
    const onPointer = (event: MouseEvent): void => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    list.current?.querySelectorAll<HTMLButtonElement>('button')[(index + step + LOCALES.length) % LOCALES.length]?.focus();
  };

  return (
    <div ref={root} className={styles.menu}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('language.label')}
        data-testid="language-menu"
        onClick={() => {
          setOpen(!open);
        }}
      >
        <GlobeIcon size={15} />
        <Name code={current.code} />
        <ChevronDownIcon size={12} className={styles.chevron} />
      </button>
      {open ? (
        <div ref={list} className={styles.popover} role="group" aria-label={t('language.label')} data-testid="language-list">
          {LOCALES.map((locale, index) => (
            <button
              key={locale.code}
              type="button"
              className={styles.item}
              aria-checked={locale.code === current.code}
              role="menuitemradio"
              onKeyDown={(event) => {
                onKeyDown(event, index);
              }}
              onClick={() => {
                setOpen(false);
                void setLocale(locale.code);
              }}
            >
              <Name code={locale.code} />
              {locale.code === current.code ? <CheckIcon size={14} /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The app's Settings: the branded drop-down (`Select`), each language named in itself. */
export function LanguageList(): React.JSX.Element {
  const current = useLocale();
  const selectId = 'language-select';
  return (
    <div className={styles.listGroup}>
      <label htmlFor={selectId} className={styles.listLabel}>
        {t('language.label')}
      </label>
      <Select
        id={selectId}
        testId="language-select"
        value={current.code}
        options={LOCALES.map((locale) => ({ value: locale.code, label: locale.name, lang: locale.code, dir: locale.dir }))}
        onChange={(code) => {
          void setLocale(code);
        }}
      />
    </div>
  );
}
